import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../config';
import '../../../styles/ModalAnnonce.scss';

const isBrokenUrl = (url) => !url || /\.(pdf|jpg|jpeg|png)_?\d+\.(pdf|jpg|jpeg|png)$/i.test(url);

function ModalAnnonce({ selectedRow }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transitaire, setTransitaire] = useState('');
  const [blFile, setBlFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);

  const hasBrokenDraft = selectedRow?.[0] === 'Conteneur' && isBrokenUrl(selectedRow?.[7]);
  const isActivating = selectedRow?.[5] === 'En attente' || selectedRow?.[5] === 'Bloqué';

  // Réinitialiser les states quand on change d'annonce
  useEffect(() => {
    setTransitaire('');
    setBlFile(null);
    setError(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [selectedRow]);

  const handleMouseMove = (e) => {
    const container = containerRef.current;
    const image = imageRef.current;

    if (!container || !image) return;

    const { left, top, width, height } = container.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100; // Position X en pourcentage
    const y = ((e.clientY - top) / height) * 100; // Position Y en pourcentage

    // Appliquer les transformations pour que le zoom suive le curseur
    image.style.transformOrigin = `${x}% ${y}%`;
  };

  const sta = (statut) => {
    switch (statut) {
      case 'En attente':
        return 'text-warning';
      case 'Activé':
        return 'text-success';
      case 'Expiré':
        return 'text-danger';
        case 'Bloqué':
          return 'text-danger';
      default:
        return statut;
    }
  } 
  const classBtn = (statut) => {
    switch (statut) {
      case 'En attente':
        return 'btn-success';
      case 'Activé':
        return 'btn-danger';
      case 'Expiré':
        return 'btn-danger';
        case 'Bloqué':
          return 'btn-success';
      default:
        return statut;
    }
  }
  const msgBtn = (statut) => {
    switch (statut) {
      case 'En attente':
        return 'Activer cette annonce';
      case 'Activé':
        return 'Bloquer cette annonce';
      case 'Bloqué':
        return 'Activer à nouveau cette annonce';
      default:
        return statut;
    }
  }

  const toggleActiveStatus = async (announcementId) => {
    if (!announcementId) {
      setError("ID de l'annonce non valide.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token introuvable. Veuillez vous reconnecter.");
      }

      let response;

      if (blFile && isActivating) {
        // Upload avec fichier BL via FormData (uniquement lors de l'activation)
        const formData = new FormData();
        const isPdf = blFile.type === 'application/pdf';
        formData.append(isPdf ? "pdf_document" : "images", blFile);
        formData.append("id", announcementId);
        if (transitaire.trim()) {
          formData.append("transitaire", transitaire.trim());
        }

        const route = isPdf ? "updateactivecontainerwithfile" : "updateactivecontainerwithimage";
        response = await fetch(
          `${API_URL}/api/annonce/${route}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );
      } else {
        const payload = { id: announcementId };
        if (transitaire.trim()) {
          payload.transitaire = transitaire.trim();
        }

        response = await fetch(
          `${API_URL}/api/annonce/updateactivecontainer`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );
      }

      if (!response.ok) {
        throw new Error(`Erreur : ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === 0) {
        alert("Statut mis à jour avec succès !");
        navigate(0); // Cela recharge la page actuelle
        // Callback optionnel pour rafraîchir l'UI
      } else {
        throw new Error(data.message || "Erreur lors de la mise à jour.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

/*   const downloadImage = () => {
    if (selectedRow[7]) {
      const link = document.createElement("a");
      link.href = selectedRow[7];
      link.download = "image_annonce";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }; */

  const saveTransitaire = async (announcementId) => {
    if (!announcementId || !transitaire.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token introuvable. Veuillez vous reconnecter.");
      }

      const response = await fetch(
        `${API_URL}/api/annonce/updatetransitaire`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: announcementId, transitaire: transitaire.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur : ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === 0) {
        alert("Transitaire ajouté avec succès !");
        navigate(0);
      } else {
        throw new Error(data.message || "Erreur lors de la mise à jour.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = async () => {
    if (selectedRow[7]) {
      try {
        const response = await fetch(selectedRow[7]);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
  
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "image_BL.png"; // Nom du fichier téléchargé
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
  
        // Nettoyer l'URL Blob après le téléchargement
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Erreur lors du téléchargement de l'image :", error);
      }
    }
  };
  

  return (
    <div className="modal fade" role="dialog" tabIndex="-1" id="detailModal">
      <div className="modal-dialog modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title text-primary" id="detailModalLabel">
              Détails de l'annonce
            </h4>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Fermer"
            ></button>
          </div>
          <div className="modal-body">
            {selectedRow ? (
              <>
                <div className="annonce-details">
                  <div className="annonce-details_badge">
                    <span className={`annonce-details_badge_type ${selectedRow[0] === 'Conteneur' ? 'annonce-details_badge_type--container' : 'annonce-details_badge_type--kilo'}`}>
                      {selectedRow[0]}
                    </span>
                    <span className={`annonce-details_badge_status ${sta(selectedRow[5])}`}>{selectedRow[5]}</span>
                  </div>
                  <div className="annonce-details_route">
                    <div className="annonce-details_route_city">
                      <span className="annonce-details_route_label">Départ</span>
                      <span className="annonce-details_route_value">{selectedRow[1]}</span>
                    </div>
                    <div className="annonce-details_route_arrow">&#10132;</div>
                    <div className="annonce-details_route_city">
                      <span className="annonce-details_route_label">Arrivée</span>
                      <span className="annonce-details_route_value">{selectedRow[2]}</span>
                    </div>
                  </div>
                  <div className="annonce-details_grid">
                    <div className="annonce-details_grid_item">
                      <span className="annonce-details_grid_item_label">Départ le</span>
                      <span className="annonce-details_grid_item_value">{selectedRow[3]}</span>
                    </div>
                    <div className="annonce-details_grid_item">
                      <span className="annonce-details_grid_item_label">Créé le</span>
                      <span className="annonce-details_grid_item_value">{selectedRow[4]}</span>
                    </div>
                    <div className="annonce-details_grid_item">
                      <span className="annonce-details_grid_item_label">Créé par</span>
                      <span className="annonce-details_grid_item_value">{selectedRow[9]}</span>
                    </div>
                    <div className="annonce-details_grid_item">
                      <span className="annonce-details_grid_item_label">Email</span>
                      <span className="annonce-details_grid_item_value annonce-details_grid_item_value--email">{selectedRow[10]}</span>
                    </div>
                    {selectedRow[12] && (
                      <div className="annonce-details_grid_item annonce-details_grid_item--full">
                        <span className="annonce-details_grid_item_label">Transitaire</span>
                        <span className="annonce-details_grid_item_value">{selectedRow[12]}</span>
                      </div>
                    )}
                  </div>
                </div>
                {(selectedRow[5] === 'En attente' || selectedRow[5] === 'Bloqué') && (
                  <div className="mb-3">
                    <label className="form-label fw-bold" style={{color: transitaire.trim() ? '#198754' : '#dc3545'}}>Nom du transitaire (obligatoire)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Saisir le nom du transitaire"
                      value={transitaire}
                      onChange={(e) => setTransitaire(e.target.value)}
                      style={{border: `2px solid ${transitaire.trim() ? '#198754' : '#dc3545'}`}}
                    />
                  </div>
                )}
                {hasBrokenDraft && (selectedRow[5] === 'En attente' || selectedRow[5] === 'Bloqué') && (
                  <div className="mb-3">
                    {!blFile && (
                      <div className="alert alert-danger py-2">
                        Le BL de cette annonce est manquant ou corrompu. Veuillez en uploader un nouveau.
                      </div>
                    )}
                    <label className="form-label fw-bold" style={{color: blFile ? '#198754' : '#dc3545'}}>Nouveau BL (obligatoire)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setBlFile(e.target.files[0] || null)}
                      style={{ display: 'none' }}
                    />
                    {!blFile ? (
                      <div
                        className={`dropzone${isDragging ? ' dropzone--active' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                          const file = e.dataTransfer.files[0];
                          if (file && /^(application\/pdf|image\/(png|jpe?g))$/i.test(file.type)) {
                            setBlFile(file);
                          }
                        }}
                      >
                        <div className="dropzone_icon">&#128206;</div>
                        <p className="dropzone_text">Glissez-déposez votre fichier ici</p>
                        <p className="dropzone_subtext">ou cliquez pour parcourir</p>
                        <span className="dropzone_formats">PDF, PNG, JPG</span>
                      </div>
                    ) : (
                      <div className="dropzone dropzone--success">
                        <div className="dropzone_preview">
                          {blFile.type === 'application/pdf' ? (
                            <iframe
                              src={URL.createObjectURL(blFile)}
                              width="100%"
                              height="350px"
                              title="Aperçu du BL PDF"
                              style={{ border: "none", borderRadius: "8px" }}
                            />
                          ) : /^image\/(png|jpe?g)$/i.test(blFile.type) ? (
                            <img
                              src={URL.createObjectURL(blFile)}
                              alt="Aperçu du BL"
                              style={{ width: "100%", maxHeight: "350px", objectFit: "contain", borderRadius: "8px" }}
                            />
                          ) : null}
                        </div>
                        <div className="dropzone_fileinfo">
                          <span className="dropzone_filename">{blFile.name}</span>
                          <span className="dropzone_filesize">{(blFile.size / 1024).toFixed(1)} Ko</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm mt-2"
                          onClick={() => { setBlFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        >
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {selectedRow[5] === 'Activé' && selectedRow[0] === 'Conteneur' && !selectedRow[12] && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">Ajouter le transitaire</label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Saisir le nom du transitaire"
                        value={transitaire}
                        onChange={(e) => setTransitaire(e.target.value)}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={() => saveTransitaire(selectedRow[8])}
                        disabled={loading || !transitaire.trim()}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {loading ? "..." : "Ajouter"}
                      </button>
                    </div>
                  </div>
                )}
                {selectedRow[0] === "Conteneur" && selectedRow[7] && (
                  <div className="showBL" ref={containerRef} onMouseMove={handleMouseMove}>
                    {/\.(png|jpe?g)$/i.test(selectedRow[7]) ? (
                    <>
                      <img
                        ref={imageRef}
                        src={selectedRow[7]}
                        alt='Bon de livraison'
                        className="zoomable"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.2s ease", // Transition fluide
                        }}
                        onMouseLeave={() => {
                          // Réinitialise la transformation au départ du curseur
                          imageRef.current.style.transform = "scale(1)";
                        }}
                        onMouseEnter={() => {
                          // Applique le zoom lorsque le curseur entre
                          imageRef.current.style.transform = "scale(2)"; // Ajustez le facteur de zoom
                        }}
                      />
                      <div className="d-flex justify-content-center">
                        <button className="btn btn-primary mt-2" onClick={downloadImage}>
                            Télécharger l'image
                        </button>
                      </div>
                    </>
                    ) : /\.(pdf)$/i.test(selectedRow[7]) ? (
                      <iframe
                        src={selectedRow[7]}
                        width="100%"
                        height="500px"
                        title="Aperçu PDF"
                        style={{ border: "none" }}
                      />
                    ) : (
                      <p>Le format de fichier n'est pas supporté.</p>
                    )}
                  </div>
                )}

              </>
            ) : (
              <p>Aucune donnée disponible.</p>
            )}
          </div>
          <div className="modal-footer">
            <button
              onClick={() => toggleActiveStatus(selectedRow?.[8])}
              disabled={loading || (selectedRow?.[0] === 'Conteneur' && (selectedRow?.[5] === 'En attente' || selectedRow?.[5] === 'Bloqué') && !transitaire.trim()) || (hasBrokenDraft && (selectedRow?.[5] === 'En attente' || selectedRow?.[5] === 'Bloqué') && !blFile)}
              className={`btn btn-primary ${classBtn(selectedRow?.[5])}`}
            >
              {loading ? "Chargement..." :  msgBtn(selectedRow?.[5])}
            </button>
            <button
              className="btn btn-light"
              type="button"
              data-bs-dismiss="modal"
            >
              Fermer
            </button>
            {error && <p className="text-danger mt-2">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalAnnonce;